import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Heart, Shield, Sparkles, User, Star, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'usehooks-ts';
import { supabase } from '../lib/supabase';
import AuthModal from '../components/AuthModal';
import AutoScrollCards from '../components/AutoScrollCards';

interface LandingContent {
  section: string;
  key: string;
  content: string;
}

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [greeting, setGreeting] = useState('');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [content, setContent] = useState<Record<string, string>>({});

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting('Good morning');
    } else if (hour >= 12 && hour < 17) {
      setGreeting('Good afternoon');
    } else {
      setGreeting('Good evening');
    }

    // Load content from Supabase
    const loadContent = async () => {
      const { data, error } = await supabase
        .from('landing_content')
        .select('*');

      if (!error && data) {
        const contentMap = data.reduce((acc: Record<string, string>, item: LandingContent) => {
          acc[`${item.section}_${item.key}`] = item.content;
          return acc;
        }, {});
        setContent(contentMap);
      }
    };

    loadContent();
  }, []);

  const handleAuthSuccess = () => {
    navigate('/dashboard');
  };

  const handleNavigation = (path: string) => {
    if (path === '/dashboard') {
      navigate('/reflections');
      return;
    }
    if (path === '/profile') {
      navigate('/welcome-back');
      return;
    }
    navigate(path);
  };

  const handleSignInClick = () => {
    setAuthMode('sign-in');
    setShowAuthModal(true);
  };

  const features = [
    {
      icon: Shield,
      title: content['features_card1_title'] || "Private by Default",
      description: content['features_card1_description'] || "Your privacy is our priority. Share freely without worry."
    },
    {
      icon: MessageCircle,
      title: content['features_card2_title'] || "Always Available",
      description: content['features_card2_description'] || "Here whenever you need someone to talk to, day or night."
    },
    {
      icon: Heart,
      title: content['features_card3_title'] || "Understanding Without Judgement",
      description: content['features_card3_description'] || "A safe space for open conversation."
    },
    {
      icon: Sparkles,
      title: content['features_card4_title'] || "Personal Reflections",
      description: content['features_card4_description'] || "Every conversation includes a thoughtful takeaway to help you grow."
    }
  ];

  const testimonials = [
    {
      quote: content['testimonials_testimonial1'] || "Echo helped me feel heard when I needed it most. It's like having a supportive friend available anytime.",
      rating: 5
    },
    {
      quote: content['testimonials_testimonial2'] || "Being able to look back at my reflections has been incredibly helpful. It shows me how far I've come and helps me understand myself better.",
      rating: 5
    },
    {
      quote: content['testimonials_testimonial3'] || "Sometimes you just need a safe space to express yourself. Echo provides exactly that, without judgment.",
      rating: 5
    },
    {
      quote: content['testimonials_testimonial4'] || "I appreciate how Echo helps me organize my thoughts. The personal reflections after each conversation give me new perspectives I hadn't considered.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col relative">
      <div className="flex-1">
        {/* Hero Section */}
        <div className="relative min-h-[90dvh] hero-gradient flex items-center justify-center pt-8 sm:pt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full px-4 sm:px-8 md:px-12"
          >
            <div className="max-w-[600px] mx-auto text-center space-y-4">
              <div className="space-y-4">
                <h1 className="text-[2.75rem] sm:text-6xl font-semibold text-gray-900 tracking-tight leading-[1.1]">
                  {greeting},
                  <br />
                  welcome to
                  <br />
                  <span className="gradient-text font-bold">{content['hero_title'] || 'Space to Think'}</span>
                </h1>
                
                <div className="flex justify-center">
                  <p className="text-[0.9rem] sm:text-xl text-gray-600 font-medium leading-[1.8] inline-flex whitespace-nowrap px-3 py-1">
                    {content['hero_subtitle'] || 'No judgement. No pressure. Always available.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/chat')}
                  className="relative group -mt-4"
                >
                  <img 
                    src="/botbutton.png" 
                    alt="Start Talking" 
                    className="w-[240px] h-auto"
                  />
                  <div className="absolute bottom-[140px] left-[50%] translate-x-[-50%] w-[140px] h-[45px] bg-gradient-to-r from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/20 group-hover:to-pink-500/20 transition-all duration-300 rounded-full" />
                </motion.button>

                <div className="space-y-2">
                  <p className="text-gray-700 text-base sm:text-lg font-normal max-w-[400px] mx-auto">
                    Your private space for open, honest
                    <br />
                    reflection — whenever you need it.
                  </p>

                  <p className="text-gray-600">
                    Already have an account?{' '}
                    <button
                      onClick={handleSignInClick}
                      className="text-[#B96EFF] hover:text-[#F2548A] font-medium transition-colors"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Section */}
        <div className="py-32 features-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <h2 className="text-4xl sm:text-5xl font-semibold bg-gradient-to-r from-[#B96EFF] to-[#F2548A] bg-clip-text text-transparent mb-6">
                {content['features_title'] || 'Your Journey, Your Space'}
              </h2>
              <p className="text-gray-600 text-xl max-w-2xl mx-auto">
                {content['features_subtitle'] || 'Experience a supportive environment designed to help you explore your thoughts and feelings.'}
              </p>
            </motion.div>

            {isDesktop ? (
              <div className="grid grid-cols-2 gap-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="feature-card bg-white rounded-2xl p-8 shadow-xl backdrop-blur-sm relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#B96EFF]/5 to-[#F2548A]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="feature-card-content">
                      <div className="feature-card-icon w-14 h-14 bg-gradient-to-r from-[#B96EFF] to-[#F2548A] rounded-xl flex items-center justify-center shadow-lg shadow-purple-200/50 animate-soft-bounce">
                        <feature.icon className="h-7 w-7 text-white" />
                      </div>
                      <h3 className="feature-card-title text-xl font-semibold text-gray-900 mb-3">
                        {feature.title}
                      </h3>
                      <p className="feature-card-description text-gray-600 text-lg leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="relative -mx-4">
                <AutoScrollCards>
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="w-[85vw] max-w-sm feature-card bg-white rounded-2xl p-6 shadow-xl backdrop-blur-sm relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B96EFF]/5 to-[#F2548A]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="feature-card-content">
                        <div className="feature-card-icon w-12 h-12 bg-gradient-to-r from-[#B96EFF] to-[#F2548A] rounded-lg flex items-center justify-center shadow-md shadow-purple-200/50 animate-soft-bounce">
                          <feature.icon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="feature-card-title text-lg font-semibold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="feature-card-description text-gray-600 text-lg leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </AutoScrollCards>
              </div>
            )}
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="py-16 testimonials-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <h2 className="text-4xl sm:text-5xl font-semibold bg-gradient-to-r from-[#B96EFF] to-[#F2548A] bg-clip-text text-transparent mb-6">
                {content['testimonials_title'] || 'Stories of Growth'}
              </h2>
              <p className="text-gray-600 text-xl max-w-2xl mx-auto">
                {content['testimonials_subtitle'] || 'Join thousands who have found clarity and support through meaningful conversations.'}
              </p>
            </motion.div>

            {isDesktop ? (
              <div className="grid grid-cols-2 gap-8">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="testimonial-card bg-white rounded-2xl p-8 shadow-xl backdrop-blur-sm relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#B96EFF]/5 to-[#F2548A]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                    <div className="feature-card-content">
                      <div className="flex mb-6">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-6 w-6 text-[#B96EFF] fill-[#B96EFF]" />
                        ))}
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="relative -mx-4">
                <AutoScrollCards>
                  {testimonials.map((testimonial, index) => (
                    <motion.div
                      key={index}
                      className="w-[85vw] max-w-sm testimonial-card bg-white rounded-2xl p-6 shadow-xl backdrop-blur-sm relative group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B96EFF]/5 to-[#F2548A]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                      <div className="feature-card-content">
                        <div className="flex mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-5 w-5 text-[#B96EFF] fill-[#B96EFF]" />
                          ))}
                        </div>
                        <p className="text-gray-700 text-lg leading-relaxed">
                          "{testimonial.quote}"
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </AutoScrollCards>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Fixed position */}
      <nav className="fixed bottom-0 left-0 right-0 w-full bg-white/80 backdrop-blur-lg border-t border-gray-100 z-[9999] md:hidden">
        <div className="w-full max-w-lg mx-auto grid grid-cols-3 gap-2 px-4 py-2 safe-area-inset-bottom">
          <button
            onClick={() => handleNavigation('/chat')}
            className="flex flex-col items-center justify-center p-2 text-gray-500 hover:text-[#B96EFF] transition-colors"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="text-xs mt-1 font-medium">Chat</span>
          </button>
          <button
            onClick={() => handleNavigation('/dashboard')}
            className="flex flex-col items-center justify-center p-2 text-gray-500 hover:text-[#B96EFF] transition-colors"
          >
            <BookOpen className="h-6 w-6" />
            <span className="text-xs mt-1 font-medium">Reflections</span>
          </button>
          <button
            onClick={() => handleNavigation('/profile')}
            className="flex flex-col items-center justify-center p-2 text-gray-500 hover:text-[#B96EFF] transition-colors"
          >
            <User className="h-6 w-6" />
            <span className="text-xs mt-1 font-medium">Profile</span>
          </button>
        </div>
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode={authMode}
      />
    </div>
  );
};

export default LandingPage;